import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: ['[data-testid="search-input"]', '[data-testid="filter-btn"]'],
    title: 'Search & Filter Resources',
    description: 'Type in the search bar to find resources by name. Tap the "Filters" button to narrow results — you can pick multiple SDOH categories, neighborhoods, languages, cost, and service types at once.',
    position: 'bottom',
  },
  {
    target: '[data-testid="add-resource-btn"]',
    title: 'Add a Resource',
    description: 'Manually add a single community resource with details like address, phone, hours, and eligibility.',
    position: 'bottom-end',
  },
  {
    target: '[data-testid="import-btn"]',
    title: 'Import from Excel/CSV',
    description: 'Bulk import resources from a spreadsheet. AI will auto-map columns and suggest SDOH categories.',
    position: 'bottom',
  },
  {
    target: '[data-testid="toggle-selection-btn"]',
    title: 'Select Resources for a List',
    description: 'Enter selection mode to pick specific resources. Selected resources can be compiled into a printable client Resource List.',
    position: 'bottom',
  },
  {
    target: '[data-testid="lang-en"]',
    title: 'Choose the Resource List Language',
    description: 'Pick a language here for the client-facing Resource List. The printed and digital list — including category titles — will appear in Spanish, Haitian Creole, Portuguese, Arabic, or Cape Verdean Creole.',
    position: 'top',
  },
  {
    target: '[data-testid="export-csv-btn"]',
    title: 'Export & Print',
    description: 'Export all resources as CSV, or use the selection bar to create a customized printable Resource List with a QR code for clients.',
    position: 'bottom',
  },
];

const ADMIN_STEPS = [
  {
    target: '[data-testid="admin-settings-btn"]',
    title: 'Admin Settings',
    description: 'Manage users, customize Resource List headers/footers, edit categories & neighborhoods, view analytics, and toggle Test Mode.',
    position: 'bottom',
  },
];

const OnboardingTour = ({ isOpen, onClose, isAdmin }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightStyle, setSpotlightStyle] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [animate, setAnimate] = useState(true);
  const rafRef = useRef(null);

  const steps = isAdmin ? [...TOUR_STEPS, ...ADMIN_STEPS] : TOUR_STEPS;
  const step = steps[currentStep];

  // All coordinates are viewport-based (overlay is fixed), so scrolling can never desync the spotlight.
  const positionTooltip = useCallback(() => {
    if (!step) return;
    const targets = Array.isArray(step.target) ? step.target : [step.target];
    const elements = targets.map(t => document.querySelector(t)).filter(Boolean);

    if (elements.length === 0) {
      setSpotlightStyle(null);
      setTooltipStyle({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const rects = elements.map(el => el.getBoundingClientRect());
    const unionRect = {
      top: Math.min(...rects.map(r => r.top)),
      left: Math.min(...rects.map(r => r.left)),
      bottom: Math.max(...rects.map(r => r.bottom)),
      right: Math.max(...rects.map(r => r.right)),
    };
    unionRect.width = unionRect.right - unionRect.left;
    unionRect.height = unionRect.bottom - unionRect.top;

    const pad = 8;

    setSpotlightStyle({
      top: unionRect.top - pad,
      left: unionRect.left - pad,
      width: unionRect.width + pad * 2,
      height: unionRect.height + pad * 2,
      borderRadius: 12,
    });

    const tooltip = {};
    const tooltipWidth = Math.min(340, window.innerWidth - 32);
    const estTooltipHeight = 220;

    switch (step.position) {
      case 'bottom':
        tooltip.top = unionRect.bottom + pad + 12;
        tooltip.left = Math.max(16, Math.min(unionRect.left + unionRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
        break;
      case 'bottom-end':
        tooltip.top = unionRect.bottom + pad + 12;
        tooltip.left = Math.max(16, unionRect.right - tooltipWidth);
        break;
      case 'top':
        tooltip.top = unionRect.top - pad - 12;
        tooltip.left = Math.max(16, Math.min(unionRect.left + unionRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
        tooltip.transform = 'translateY(-100%)';
        break;
      default:
        tooltip.top = unionRect.bottom + pad + 12;
        tooltip.left = Math.max(16, unionRect.left);
    }
    // Keep tooltip inside the viewport
    if (!tooltip.transform) {
      tooltip.top = Math.min(tooltip.top, window.innerHeight - estTooltipHeight - 12);
    }
    tooltip.top = Math.max(12, tooltip.top);
    tooltip.width = tooltipWidth;
    setTooltipStyle(tooltip);
  }, [step]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !step) return;
    setAnimate(true);

    // Bring the target into view before positioning
    const targets = Array.isArray(step.target) ? step.target : [step.target];
    const el = document.querySelector(targets[0]);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < 72 || r.bottom > window.innerHeight - 180) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
    const timer = setTimeout(positionTooltip, 60);

    // While scrolling/resizing, reposition every frame with animation off so the spotlight never lags behind
    const onScrollOrResize = () => {
      setAnimate(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(positionTooltip);
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [isOpen, currentStep, positionTooltip, step]);

  if (!isOpen || !step) return null;

  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;
  const transition = animate ? 'all 0.3s ease' : 'none';

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour">
      {/* Dark overlay with spotlight cutout via SVG (viewport-fixed) */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightStyle && (
              <rect
                x={spotlightStyle.left}
                y={spotlightStyle.top}
                width={spotlightStyle.width}
                height={spotlightStyle.height}
                rx={spotlightStyle.borderRadius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring */}
      {spotlightStyle && (
        <div
          className="absolute border-2 border-[#5C4023] pointer-events-none"
          style={{
            top: spotlightStyle.top,
            left: spotlightStyle.left,
            width: spotlightStyle.width,
            height: spotlightStyle.height,
            borderRadius: spotlightStyle.borderRadius,
            boxShadow: '0 0 0 4px rgba(92, 64, 35, 0.25)',
            transition,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-2xl shadow-2xl border border-[#E0D6C8] p-5 z-10"
        style={{ ...tooltipStyle, transition }}
        data-testid="tour-tooltip"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#5C4023] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-medium text-[#888]">
              {currentStep + 1} of {steps.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="tour-close-btn"
            className="h-7 w-7 rounded-full hover:bg-[#F3EFE9] text-[#888]"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <h3 className="font-bold text-[#1A1A1A] font-['Manrope'] text-base mb-1.5">
          {step.title}
        </h3>
        <p className="text-sm text-[#5A5A5A] leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Progress dots + navigation */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={`step-${steps[i]?.title || i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep ? 'w-6 bg-[#5C4023]' : 'w-1.5 bg-[#E0D6C8]'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep(currentStep - 1)}
                data-testid="tour-prev-btn"
                className="h-8 px-3 rounded-full text-[#5A5A5A] hover:bg-[#F3EFE9]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => isLast ? onClose() : setCurrentStep(currentStep + 1)}
              data-testid="tour-next-btn"
              className="h-8 px-4 rounded-full bg-[#5C4023] hover:bg-[#4A331C] text-white"
            >
              {isLast ? 'Got it!' : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
